import { inject, Injectable, signal } from '@angular/core';
import { createClient, LiveClient, LiveTranscriptionEvents } from '@deepgram/sdk';
import { firstValueFrom, Subject } from 'rxjs';
import { TranscriptionRepository } from '@xmedical/core';
import { TRANSCRIBE_ERROR_HANDLER, TranscribeErrorHandler } from './error-handler.token';

/**
 * Deepgram streaming transcription service
 *
 * Handles real-time audio transcription using Deepgram's SDK
 * Connects directly to Deepgram using API key from backend
 */
@Injectable()
export class DeepgramStreamingService {
  private transcriptionRepo = inject(TranscriptionRepository);
  private errorHandler: TranscribeErrorHandler = inject(TRANSCRIBE_ERROR_HANDLER, { optional: true })
    ?? { error: (msg, err) => console.error(`[Transcribe] ${msg}`, err) };

  // Deepgram client and connection
  private deepgramClient?: ReturnType<typeof createClient>;
  private liveConnection?: LiveClient;
  private cachedApiKey?: string; // Cache API key in memory

  // State signals
  readonly connected = signal<boolean>(false);
  readonly finalizing = signal<boolean>(false);
  readonly transcriptionText = signal<string>('');
  readonly apiKeyReady = signal<boolean>(false);
  readonly apiKeyLoading = signal<boolean>(false);

  // Observable streams
  private readonly transcriptionSubject = new Subject<{ text: string; isFinal: boolean; confidence: number }>();
  /** Emits transcription results as they arrive */
  readonly transcription$ = this.transcriptionSubject.asObservable();

  private readonly errorSubject = new Subject<string>();
  /** Emits transcription errors */
  readonly error$ = this.errorSubject.asObservable();

  /**
   * Pre-fetch API key from backend without establishing connection
   * Call this early (e.g., when permission is granted) to reduce latency when starting recording
   */
  async prefetchApiKey(): Promise<void> {
    // Already have key or currently loading
    if (this.cachedApiKey || this.apiKeyLoading()) {
      return;
    }

    this.apiKeyLoading.set(true);
    try {
      const creds = await firstValueFrom(this.transcriptionRepo.getTranscriptionCredentials());
      this.cachedApiKey = creds.apiKey;

      if (!this.cachedApiKey) {
        throw new Error('Failed to get Deepgram API key from backend');
      }

      this.apiKeyReady.set(true);
    } catch (error: unknown) {
      this.errorHandler.error('Failed to prefetch API key', error);
      throw error;
    } finally {
      this.apiKeyLoading.set(false);
    }
  }

  /**
   * Initialize connection to Deepgram
   */
  async connect(): Promise<void> {
    try {
      // Clear any accumulated text from a previous session
      this.reset();

      // Get API key from cache or fetch from backend
      if (!this.cachedApiKey) {
        await this.prefetchApiKey();
      }

      // Create Deepgram client
      this.deepgramClient = createClient(this.cachedApiKey);

      // Create live transcription connection
      // Don't specify encoding - let Deepgram auto-detect from WebM container
      this.liveConnection = this.deepgramClient.listen.live({
        model: 'nova-3-medical',
        language: 'en',
        // smart_format includes punctuation, numerals, and paragraphs
        smart_format: true,
        dictation: true,
        channels: 1,
        interim_results: true,
        utterance_end_ms: 1000,
        vad_events: true,
      });

      // Set up event handlers
      this.setupEventHandlers();

      // Wait for connection to open
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Connection timeout'));
        }, 10000);

        this.liveConnection!.on(LiveTranscriptionEvents.Open, () => {
          clearTimeout(timeout);
          this.connected.set(true);
          resolve();
        });

        this.liveConnection!.on(LiveTranscriptionEvents.Error, (error: unknown) => {
          clearTimeout(timeout);
          reject(error);
        });
      });

    } catch (error: unknown) {
      this.errorHandler.error('Failed to connect to Deepgram', error);
      throw error;
    }
  }

  /**
   * Send audio chunk to Deepgram
   */
  sendAudio(audioBlob: Blob): void {
    if (!this.liveConnection || !this.connected()) {
      return;
    }
    // Convert Blob to ArrayBuffer and send
    audioBlob.arrayBuffer().then(buffer => {
      this.liveConnection!.send(buffer);
    }).catch(error => {
      this.errorHandler.error('Failed to send audio to Deepgram', error);
    });
  }

  /**
   * Close connection to Deepgram
   * Flushes the server's transcription buffer before closing to ensure
   * all formatted results (punctuation, smart_format) are received.
   */
  async disconnect(): Promise<void> {
    if (this.liveConnection) {
      try {
        this.finalizing.set(true);

        // Arm the listener before flushing so a synchronous from_finalize event isn't missed
        const finalizedPromise = this.waitForFinalizedResult();

        // Flush any transcription sitting in the server's buffer
        this.liveConnection.finalize();

        // Wait for the finalized result (from_finalize: true) or timeout after 5s
        await finalizedPromise;

        // Request the server close the connection
        this.liveConnection.requestClose();

        this.connected.set(false);
      } catch (error: unknown) {
        this.errorHandler.error('Error closing Deepgram connection', error);
      } finally {
        this.finalizing.set(false);
      }
    }

    this.liveConnection = undefined;
    this.deepgramClient = undefined;
  }

  /**
   * Wait for Deepgram to respond with the finalized transcript.
   * Resolves when a transcript with from_finalize: true arrives, or after 5s timeout.
   */
  private waitForFinalizedResult(): Promise<void> {
    return new Promise<void>(resolve => {
      if (!this.liveConnection) {
        resolve();
        return;
      }

      let settled = false;
      const connection = this.liveConnection;

      const settle = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        connection.removeListener(LiveTranscriptionEvents.Transcript, handler);
        resolve();
      };

      const timeout = setTimeout(settle, 5000);

      const handler = (data: { from_finalize?: boolean }) => {
        if (data.from_finalize) {
          settle();
        }
      };

      connection.on(LiveTranscriptionEvents.Transcript, handler);
    });
  }

  /**
   * Reset service state
   */
  reset(): void {
    this.transcriptionText.set('');
  }

  /**
   * Set up Deepgram event handlers
   */
  private setupEventHandlers(): void {
    if (!this.liveConnection) {
      return;
    }

    // Transcript event - receives transcription results
    this.liveConnection.on(LiveTranscriptionEvents.Transcript, (data: {
      channel?: { alternatives?: Array<{ transcript?: string; confidence?: number }> };
      is_final?: boolean;
    }) => {
      const transcript = data.channel?.alternatives?.[0]?.transcript;
      const isFinal = data.is_final ?? false;
      const confidence = data.channel?.alternatives?.[0]?.confidence ?? 0;

      if (transcript && transcript.trim().length > 0) {
        // Emit the transcription result
        this.transcriptionSubject.next({
          text: transcript,
          isFinal,
          confidence
        });

        // Update accumulated text if final
        if (isFinal) {
          const currentText = this.transcriptionText();
          const newText = currentText ? `${currentText} ${transcript}` : transcript;
          this.transcriptionText.set(newText);
        }
      }
    });

    // Metadata event
    this.liveConnection.on(LiveTranscriptionEvents.Metadata, () => {
      // Metadata received - no action needed
    });

    // Error event
    this.liveConnection.on(LiveTranscriptionEvents.Error, (error: { message?: string }) => {
      this.errorSubject.next(error.message || 'Transcription error');
      this.errorHandler.error('Deepgram error', error);
    });

    // Close event
    this.liveConnection.on(LiveTranscriptionEvents.Close, () => {
      this.connected.set(false);
    });

    // UtteranceEnd event - triggers when user stops speaking
    this.liveConnection.on(LiveTranscriptionEvents.UtteranceEnd, () => {
      // Utterance ended - no action needed
    });
  }
}
